import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';import { ArrowRight, Shield, Clock, Award, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="services-page">
      <SEO title="Servicii Hidroizolatii" description="Servicii profesionale de hidroizolatii in Iasi si Moldova. Montaj membrane bituminoase, EPDM, reparatii infiltratii, injectari rasina. Echipa experimentata." canonical="/servicii" />
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4 bg-gradient-to-r from-cyan-600 to-cyan-800 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl lg:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="services-title"
          >
            Servicii
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto"
          >
            Descoperă serviciile noastre de hidroizolații de top! Echipă specializată în impermeabilizări pentru case și clădiri. Calitate garantată.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/vreau-oferta">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-full shadow-lg" data-testid="hero-cta">
                Intră în contact cu noi
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="py-8 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Shield className="w-10 h-10 text-cyan-600 mb-2" />
              <span className="font-bold text-slate-900">Garanție până la 20 ani</span>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-10 h-10 text-cyan-600 mb-2" />
              <span className="font-bold text-slate-900">Răspuns în 24h</span>
            </div>
            <div className="flex flex-col items-center">
              <Award className="w-10 h-10 text-cyan-600 mb-2" />
              <span className="font-bold text-slate-900">Materiale Premium</span>
            </div>
            <div className="flex flex-col items-center">
              <Phone className="w-10 h-10 text-cyan-600 mb-2" />
              <span className="font-bold text-slate-900">Consultanță Gratuită</span>
            </div>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={`/serviciu/${service.id}`}
                    className="block glass-card overflow-hidden hover:shadow-xl transition-all duration-300 group"
                    data-testid={`service-${service.id}`}
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="md:w-1/3 h-64 md:h-auto relative overflow-hidden">
                        <img
                          src={service.image_url}
                          alt={service.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                          Garanție {service.warranty}
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="md:w-2/3 p-8 flex flex-col justify-between">
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-cyan-600 transition" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {service.name}
                          </h2>
                          <p className="text-slate-600 mb-4">
                            {service.short_description}
                          </p>
                          <p className="text-slate-500 text-sm">
                            {service.description}
                          </p>
                        </div>
                        
                        <div className="mt-6 flex items-center justify-between">
                          <span className="text-sm text-slate-500">
                            {service.phases?.length || 0} etape de lucru
                          </span>
                          <Button className="btn-primary" data-testid={`cta-${service.id}`}>
                            Informații serviciu
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ai nevoie de o ofertă personalizată?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Completează formularul nostru și primești oferta în maxim 24 de ore!
          </p>
          <Link to="/vreau-oferta">
            <Button className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg rounded-full shadow-lg" data-testid="bottom-cta">
              VREAU OFERTĂ
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Services;
